import * as THREE from 'three';
import { physicsWorld, getRapier, wakeObjectsInRadius } from './physics.js';
import { playShootSound, playImpactSound, playHitSound } from './audio.js';
import { triggerSVGHit } from './objects-svg.js';
import { svgObjects, rigidBodies } from './objects-shared.js';
import { getHTMLPages } from './objects-page.js';
import { config } from './config.js';
import { 
    initMobileControls, 
    updateMobileControls, 
    getMobileMovement, 
    getMobileLook,
    getMobileRaycast,
    isMobileDevice
} from './mobile-controls.js';

// ✅ OPTYMALIZACJA 1: Object pooling dla projectiles
const PROJECTILE_POOL_SIZE = 20;
const projectilePool = [];
const projectileGeometry = new THREE.SphereGeometry(0.5, 12, 12); // Niższa jakość - 12 zamiast 16
const projectileMaterial = new THREE.MeshStandardMaterial({
    color: 0x8fffc7,
    metalness: 0.2,
    roughness: 0.6,
    emissive: 0x8fffff,
    emissiveIntensity: 0.7
});

export const projectiles = [];
export let playerBody = null;
export let playerYaw = 0;
export let playerPitch = 0;

const playerStart = new THREE.Vector3(0, 15, 0);
const keys = {};
let jumpChargeTime = 0;
let isChargingJump = false;

let mouseX = 0;
let mouseY = 0;
let targetLookX = 0;
let targetLookY = 0;
let currentLookX = 0;
let currentLookY = 0;
let scrollVelocity = 0;

// ✅ OPTYMALIZACJA 2: Raycast pooling
let raycaster = null;
let currentHoveredSVG = null;
let raycastPoint = new THREE.Vector3();
let mouseScreenX = 0;
let mouseScreenY = 0;

// ✅ OPTYMALIZACJA 3: Time-based raycast zamiast frame-based
let raycastAccumulator = 0;
const RAYCAST_INTERVAL = 0.1; // 100ms = 10 razy na sekundę

let currentCamera = null;

// ✅ OPTYMALIZACJA 4: Reusable vectors (garbage collection reduction)
const tempForward = new THREE.Vector3();
const tempRight = new THREE.Vector3();
const tempMove = new THREE.Vector3();
const tempYawQuat = new THREE.Quaternion();
const tempLookQuat = new THREE.Quaternion();
const tempVector = new THREE.Vector3();

export function createPlayer() {
    const RAPIER = getRapier();
    
    const desc = RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(playerStart.x, playerStart.y, playerStart.z)
        .setLinearDamping(0)
        .setAngularDamping(10)
        .setCanSleep(false) // Gracz nigdy nie śpi
        .setCcdEnabled(true) // Continuous Collision Detection dla szybkiego ruchu
        .setGravityScale(config.player.gravity);
    playerBody = physicsWorld.createRigidBody(desc);

    const col = RAPIER.ColliderDesc.ball(0.3)
        .setRestitution(0.3)
        .setFriction(1.0)
        .setDensity(1.0);    
      
    physicsWorld.createCollider(col, playerBody);
    
    raycaster = new THREE.Raycaster();
    raycaster.far = 1000; // Ogranicz zasięg raycast
    
    // ✅ Pre-warm projectile pool
    initProjectilePool();
}

// ✅ NOWA FUNKCJA: Inicjalizacja pool'a
function initProjectilePool() {
    for (let i = 0; i < PROJECTILE_POOL_SIZE; i++) {
        const proj = new THREE.Mesh(projectileGeometry, projectileMaterial);
        proj.castShadow = true;
        proj.visible = false;
        projectilePool.push(proj);
    }
    console.log(`Projectile pool initialized with ${PROJECTILE_POOL_SIZE} objects`);
}

// ✅ NOWA FUNKCJA: Pobierz projectile z pool'a
function getProjectileFromPool() {
    let proj = projectilePool.pop();
    
    if (!proj) {
        // Pool pusty - utwórz nowy (rzadko się zdarza)
        console.warn('Projectile pool exhausted, creating new projectile');
        proj = new THREE.Mesh(projectileGeometry, projectileMaterial);
        proj.castShadow = true;
    }
    
    proj.visible = true;
    return proj;
}

// ✅ NOWA FUNKCJA: Zwróć projectile do pool'a
function returnProjectileToPool(proj) {
    if (projectilePool.length < PROJECTILE_POOL_SIZE * 2) { // Max pool size
        proj.visible = false;
        
        // Reset userData
        proj.userData.hasHitSVG = false;
        proj.userData.hasHitOther = false;
        proj.userData.hasHitImage = false;
        proj.userData.scene = null;
        
        projectilePool.push(proj);
    } else {
        // Pool jest pełny, nie potrzebujemy więcej obiektów
        // Nie dispose geometry/material bo są współdzielone
        if (proj.userData.scene) {
            proj.userData.scene.remove(proj);
        }
    }
}

export function setupControls(renderer) {
    initMobileControls(playerBody, playerStart, fireProjectile);
    setupDesktopControls(renderer);
    setupJumpControls();
}

function setupDesktopControls(renderer) {
    window.addEventListener('keydown', e => {
        if (keys[e.code]) return;
        keys[e.code] = true;
        
        if (e.code === 'KeyR') {
            playerBody.setTranslation(playerStart, true);
            playerBody.setLinvel({ x: 0, y: 0, z: 0 }, true);
            scrollVelocity = 0;
        }
    });

    window.addEventListener('keyup', e => {
        keys[e.code] = false;
    });

    // ✅ OPTYMALIZACJA 5: Throttled mouse move
    let mouseMoveTimeout;
    window.addEventListener('mousemove', e => {
        mouseScreenX = (e.clientX / window.innerWidth) * 2 - 1;
        mouseScreenY = -(e.clientY / window.innerHeight) * 2 + 1;
        
        const yawMult = config.camera.invertYaw ? 1 : -1;
        const pitchMult = config.camera.invertPitch ? -1 : 1;
        
        mouseX = mouseScreenX * yawMult;
        mouseY = mouseScreenY * pitchMult;
        
        targetLookX = mouseX * config.camera.maxLookAngle;
        targetLookY = mouseY * config.camera.maxLookAngle * 0.5;
    });

    window.addEventListener('wheel', e => {
        e.preventDefault();
        const scrollMult = config.camera.invertScroll ? -1 : 1;
        scrollVelocity += e.deltaY * config.camera.scrollSpeed * 0.001 * scrollMult;
    }, { passive: false });

    window.addEventListener('mousedown', async e => {
        if (e.button === 0) {
            fireProjectile();
        }
        if (e.button === 2 && config.magnet.enabled) {
            const { startMagnetPull } = await import('./magnet.js');
            startMagnetPull();
            e.preventDefault();
        }
    });

    window.addEventListener('mouseup', async e => {
        if (e.button === 2 && config.magnet.enabled) {
            const { stopMagnetPull } = await import('./magnet.js');
            stopMagnetPull();
            e.preventDefault();
        }
    });

    window.addEventListener('contextmenu', e => {
        e.preventDefault();
    });

    renderer.domElement.addEventListener('touchstart', (e) => {}, { passive: true });
    renderer.domElement.addEventListener('touchmove', (e) => {}, { passive: true });
}

function setupJumpControls() {
    const hudElement = document.getElementById('hud');
    
    if (isMobileDevice() && hudElement) {
        hudElement.addEventListener('touchstart', (e) => {
            e.preventDefault();
            if (!playerBody) return;
            const currentVel = playerBody.linvel();
            if (Math.abs(currentVel.y) < 0.5) {
                isChargingJump = true;
                jumpChargeTime = 0;
            }
        });
        hudElement.addEventListener('touchend', (e) => {
            e.preventDefault();
            if (isChargingJump) {
                performJump();
            }
        });
    }

    if (!isMobileDevice() && hudElement) {
        hudElement.addEventListener('mousedown', (e) => {
            e.preventDefault();
            if (!playerBody) return;
            const currentVel = playerBody.linvel();
            if (Math.abs(currentVel.y) < 0.5) {
                isChargingJump = true;
                jumpChargeTime = 0;
            }
        });
        hudElement.addEventListener('mouseup', (e) => {
            e.preventDefault();
            if (isChargingJump) {
                performJump();
            }
        });
    }

    window.addEventListener('keydown', e => {
        if (e.code === 'Space') {
            if (!isChargingJump) {
                if (!playerBody) return;
                const currentVel = playerBody.linvel();
                if (Math.abs(currentVel.y) < 0.5) {
                    isChargingJump = true;
                    jumpChargeTime = 0;
                }
            }
            e.preventDefault();
        }
        
        if (e.code === 'KeyQ') {
            if (!keys['KeyQ']) {
                teleportToHoveredImage();
                keys['KeyQ'] = true;
            }
            e.preventDefault();
        }
    });

    window.addEventListener('keyup', e => {
        if (e.code === 'Space') {
            if (isChargingJump) {
                performJump();
            }
            e.preventDefault();
        }
        
        if (e.code === 'KeyQ') {
            keys['KeyQ'] = false;
        }
    });
}

function teleportToHoveredImage() {
    if (!raycaster || !currentCamera || !playerBody) return;
    
    import('./objects-images.js').then(({ getImageObjects, teleportToImage }) => {
        const imageObjects = getImageObjects();
        if (!imageObjects || imageObjects.length === 0) return;
        
        raycaster.setFromCamera(new THREE.Vector2(mouseScreenX, mouseScreenY), currentCamera);
        const intersects = raycaster.intersectObjects(imageObjects, false);
        
        if (intersects.length > 0) {
            const imageObject = intersects[0].object;
            if (imageObject.userData.isImage) {
                teleportToImage(imageObject, playerBody, currentCamera);
            }
        }
    }).catch(err => {
        console.error('Failed to load objects-images module:', err);
    });
}

function performJump() {
    const chargePercent = Math.min(jumpChargeTime / config.player.chargeTime, 1.0);
    const jumpForce = config.player.jumpMin + (config.player.jumpMax - config.player.jumpMin) * chargePercent;
    
    playerBody.applyImpulse({ x: 0, y: jumpForce, z: 0 }, true);
    
    // ✅ Obudź obiekty w pobliżu po skoku
    const pos = playerBody.translation();
    wakeObjectsInRadius(pos, 10);
    
    isChargingJump = false;
    jumpChargeTime = 0;
}

function fireProjectile() {
    if (!currentCamera) return;
    
    const playerPos = playerBody.translation();
    tempVector.set(playerPos.x, playerPos.y + config.camera.height, playerPos.z);
    
    // ✅ OPTYMALIZACJA 6: Reuse vectors
    if (isMobileDevice()) {
        const mobileRaycast = getMobileRaycast();
        raycaster.setFromCamera(new THREE.Vector2(mobileRaycast.x, mobileRaycast.y), currentCamera);
        const intersects = raycaster.intersectObjects(svgObjects, true);
        
        if (intersects.length > 0) {
            tempForward.copy(intersects[0].point).sub(tempVector).normalize();
        } else {
            tempLookQuat.setFromEuler(new THREE.Euler(currentLookY, currentLookX, 0, 'YXZ'));
            tempForward.set(0, 0, -1).applyQuaternion(tempLookQuat);
        }
    } else {
        raycaster.setFromCamera(new THREE.Vector2(mouseScreenX, mouseScreenY), currentCamera);
        const intersects = raycaster.intersectObjects(svgObjects, true);
        
        if (intersects.length > 0) {
            tempForward.copy(intersects[0].point).sub(tempVector).normalize();
        } else {
            tempLookQuat.setFromEuler(new THREE.Euler(currentLookY, currentLookX, 0, 'YXZ'));
            tempForward.set(0, 0, -1).applyQuaternion(tempLookQuat);
        }
    }

    // ✅ Pobierz z pool'a zamiast tworzyć nowy
    const proj = getProjectileFromPool();
    
    proj.position.copy(tempVector).add(tempForward.clone().multiplyScalar(2));

    const RAPIER = getRapier();
    const pDesc = RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(proj.position.x, proj.position.y, proj.position.z)
        .setGravityScale(config.projectile.gravityScale)
        .setLinearDamping(0.1)
        .setAngularDamping(0.5)
        .setCanSleep(true) // Projectile może spać
        .setCcdEnabled(true); // CCD dla szybkich projektili
        
    const pBody = physicsWorld.createRigidBody(pDesc);
    const pCol = RAPIER.ColliderDesc.ball(0.4)
        .setRestitution(0.6)
        .setFriction(0.6)
        .setDensity(30.0);
    physicsWorld.createCollider(pCol, pBody);

    pBody.setLinvel({ 
        x: tempForward.x * config.projectile.speed, 
        y: tempForward.y * config.projectile.speed, 
        z: tempForward.z * config.projectile.speed 
    }, true);

    proj.userData.physicsBody = pBody;
    proj.userData.createdAt = performance.now();
    proj.userData.hasHitSVG = false;
    proj.userData.hasHitOther = false;
    proj.userData.hasHitImage = false;
    proj.userData.scene = null;
    
    // ✅ OPTYMALIZACJA 7: Tag physics body dla szybszego lookup
    pBody.userData = { 
        type: 'projectile',
        meshReference: proj
    };
    
    projectiles.push(proj);

    playShootSound();
    
    return proj;
}

export function updatePlayer(dt, camera) {
    currentCamera = camera;
    
    updateMobileControls(dt);
    
    if (isMobileDevice()) {
        const mobileLook = getMobileLook();
        targetLookX = mobileLook.x;
        targetLookY = mobileLook.y;
    }
    
    currentLookX += (targetLookX - currentLookX) * config.camera.lookSmoothing;
    currentLookY += (targetLookY - currentLookY) * config.camera.lookSmoothing;
    
    playerYaw = currentLookX;
    playerPitch = currentLookY;

    if (isMobileDevice()) {
        scrollVelocity *= 0.95;
    } else {
        scrollVelocity *= config.camera.scrollDamping;
    }

    // ✅ OPTYMALIZACJA 8: Reuse vectors zamiast tworzyć nowe
    tempForward.set(0, 0, -1);
    tempRight.set(1, 0, 0);
    tempYawQuat.setFromAxisAngle(new THREE.Vector3(0, 1, 0), currentLookX);
    tempForward.applyQuaternion(tempYawQuat);
    tempRight.applyQuaternion(tempYawQuat);
    
    if (isMobileDevice()) {
        const mobileMove = getMobileMovement();
        if (Math.abs(mobileMove.x) > 0.01 || Math.abs(mobileMove.y) > 0.01) {
            const moveForward = mobileMove.y * config.player.touchMoveSpeed;
            const moveRight = mobileMove.x * config.player.touchMoveSpeed;
            
            playerBody.applyImpulse({
                x: tempForward.x * moveForward + tempRight.x * moveRight,
                y: 0,
                z: tempForward.z * moveForward + tempRight.z * moveRight
            }, true);
        }
    }
    
    const moveForce = scrollVelocity * config.camera.lookSensitivity;
    
    if (Math.abs(moveForce) > 0.001) {
        playerBody.applyImpulse({
            x: tempForward.x * moveForce,
            y: 0,
            z: tempForward.z * moveForce
        }, true);
    }

    // Keyboard movement
    tempMove.set(0, 0, 0);
    
    if (keys['KeyW']) tempMove.add(tempForward);
    if (keys['KeyS']) tempMove.sub(tempForward);
    if (keys['KeyA']) tempMove.sub(tempRight);
    if (keys['KeyD']) tempMove.add(tempRight);
    
    if (tempMove.lengthSq() > 0) {
        tempMove.normalize().multiplyScalar(config.player.keyboardMoveSpeed);
        playerBody.applyImpulse({
            x: tempMove.x,
            y: 0,
            z: tempMove.z
        }, true);
    }

    // Jump charge visualization
    if (isChargingJump) {
        jumpChargeTime += dt;
        const chargePercent = Math.min(jumpChargeTime / config.player.chargeTime, 1.0);
        const turboElement = document.getElementById('turbo');
        if (turboElement) {
            turboElement.style.width = Math.floor(chargePercent * 100) + '%';
        }
    } else {
        const turboElement = document.getElementById('turbo');
        if (turboElement) {
            turboElement.style.width = '0%';
        }
    }

    const playerPos = playerBody.translation();
    
    camera.position.set(
        playerPos.x,
        playerPos.y + config.camera.height,
        playerPos.z
    );
    
    camera.rotation.order = 'YXZ';
    camera.rotation.y = currentLookX;
    camera.rotation.x = currentLookY;
    
    // ✅ OPTYMALIZACJA 9: Time-based raycast
    raycastAccumulator += dt;
    if (raycastAccumulator >= RAYCAST_INTERVAL) {
        performRaycast(camera);
        raycastAccumulator = 0;
    }
}

function performRaycast(camera) {
    if (!raycaster) return;
    
    let rayVector;
    if (isMobileDevice()) {
        const mobileRaycast = getMobileRaycast();
        rayVector = new THREE.Vector2(mobileRaycast.x, mobileRaycast.y);
    } else {
        rayVector = new THREE.Vector2(mouseScreenX, mouseScreenY);
    }
    
    raycaster.setFromCamera(rayVector, camera);
    
    const htmlPages = getHTMLPages();
    const intersects = raycaster.intersectObjects(svgObjects, true);
    const pageIntersects = raycaster.intersectObjects(htmlPages, false);

    const allIntersects = [...intersects, ...pageIntersects].sort((a, b) => a.distance - b.distance);
    
    if (config.svg.raycastHighlight) {
        if (currentHoveredSVG && (!intersects.length || intersects[0].object.parent !== currentHoveredSVG)) {
            if (currentHoveredSVG.userData.materials) {
                currentHoveredSVG.userData.materials.forEach(mat => {
                    mat.emissiveIntensity = config.svg.emissiveIntensityNormal;
                });
            }
            currentHoveredSVG = null;
        }
        
        if (intersects.length > 0) {
            const hitObject = intersects[0].object;
            const svgGroup = hitObject.parent;
            
            if (svgGroup && svgGroup.userData.isSVG) {
                raycastPoint.copy(intersects[0].point);
                
                if (currentHoveredSVG !== svgGroup) {
                    currentHoveredSVG = svgGroup;
                    
                    if (svgGroup.userData.materials) {
                        svgGroup.userData.materials.forEach(mat => {
                            mat.emissiveIntensity = config.svg.emissiveIntensityHit * 0.25;
                        });
                    }
                }
            }
        }
    } else {
        if (currentHoveredSVG) {
            if (currentHoveredSVG.userData.materials) {
                currentHoveredSVG.userData.materials.forEach(mat => {
                    mat.emissiveIntensity = config.svg.emissiveIntensityNormal;
                });
            }
            currentHoveredSVG = null;
        }
        
        if (intersects.length > 0) {
            const hitObject = intersects[0].object;
            const svgGroup = hitObject.parent;
            if (svgGroup && svgGroup.userData.isSVG) {
                raycastPoint.copy(intersects[0].point);
            }
        }
    }
}

// ✅ OPTYMALIZACJA 10: Optimized collision detection z userData tags
export function updateProjectiles(scene) {
    projectiles.forEach(proj => {
        if (!proj.userData.scene && scene) {
            scene.add(proj);
            proj.userData.scene = scene;
        }

        if (proj.userData.physicsBody) {
            const projBody = proj.userData.physicsBody;
            
            // ✅ Używamy userData.type dla szybkiego rozpoznania
            physicsWorld.contactsWith(projBody.collider(0), (otherCollider) => {
                const otherBody = otherCollider.parent();
                const userData = otherBody.userData;
                
                if (!userData) return;
                
                // Szybkie sprawdzenie typu zamiast iteracji przez wszystkie obiekty
                if (userData.type === 'svg' && !proj.userData.hasHitSVG) {
                    playImpactSound();
                    triggerSVGHit(userData.meshReference);
                    proj.userData.hasHitSVG = true;
                    return;
                }
                
                if (userData.type === 'image' && !proj.userData.hasHitImage) {
                    import('./objects-images.js').then(({ triggerImageHit }) => {
                        playImpactSound();
                        triggerImageHit(userData.meshReference);
                        proj.userData.hasHitImage = true;
                    });
                    return;
                }
                
                if (userData.type === 'cylinder' && !proj.userData.hasHitOther) {
                    playHitSound();
                    proj.userData.hasHitOther = true;
                    return;
                }
                
                // Fallback dla starych obiektów bez userData.type
                // (można usunąć po aktualizacji wszystkich obiektów)
                if (!userData.type) {
                    // Legacy collision detection
                    for (const svgObj of svgObjects) {
                        if (svgObj.userData.physicsBody.handle === otherBody.handle && !proj.userData.hasHitSVG) {
                            playImpactSound();
                            triggerSVGHit(svgObj);
                            proj.userData.hasHitSVG = true;
                            return;
                        }
                    }
                    
                    for (const rigidObj of rigidBodies) {
                        if (rigidObj.userData.isCylinder && rigidObj.userData.physicsBody.handle === otherBody.handle && !proj.userData.hasHitOther) {
                            playHitSound();
                            proj.userData.hasHitOther = true;
                            return;
                        }
                    }
                }
            });
        }
    });

    // ✅ Cleanup z użyciem pool'a
    for (let i = projectiles.length - 1; i >= 0; i--) {
        const proj = projectiles[i];
        const pb = proj.userData.physicsBody;
        
        if (pb) {
            const pp = pb.translation();
            const pr = pb.rotation();
            proj.position.set(pp.x, pp.y, pp.z);
            proj.quaternion.set(pr.x, pr.y, pr.z, pr.w);
        }
        
        if (performance.now() - proj.userData.createdAt > config.projectile.lifetime || proj.position.y < -100) {
            if (proj.userData.scene) {
                proj.userData.scene.remove(proj);
            }
            
            // ✅ Zwróć do pool'a zamiast dispose
            if (pb) {
                physicsWorld.removeRigidBody(pb);
            }
            
            returnProjectileToPool(proj);
            projectiles.splice(i, 1);
        }
    }
}

// ✅ NOWA FUNKCJA: Cleanup on scene unload
export function cleanupPlayer() {
    // Zwróć wszystkie aktywne projectile do pool'a
    projectiles.forEach(proj => {
        if (proj.userData.scene) {
            proj.userData.scene.remove(proj);
        }
        if (proj.userData.physicsBody) {
            physicsWorld.removeRigidBody(proj.userData.physicsBody);
        }
        returnProjectileToPool(proj);
    });
    projectiles.length = 0;
    
    console.log('Player cleanup complete');
}

// ✅ NOWA FUNKCJA: Get player stats
export function getPlayerStats() {
    if (!playerBody) return null;
    
    const pos = playerBody.translation();
    const vel = playerBody.linvel();
    
    return {
        position: { x: pos.x, y: pos.y, z: pos.z },
        velocity: { x: vel.x, y: vel.y, z: vel.z },
        speed: Math.sqrt(vel.x * vel.x + vel.y * vel.y + vel.z * vel.z),
        yaw: playerYaw,
        pitch: playerPitch,
        activeProjectiles: projectiles.length,
        poolSize: projectilePool.length
    };
}